import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BarLoader } from 'react-spinners';
import theme from '@/shared/theme/index.module.scss';
import authService from '@/core/auth/services/auth-service';
import { analyticsService, AnalyticsEvent } from '@/shared/analytics/export';

import './bbg/bb-apps-authentication';

interface BBTokens {
  idToken: {
    sub: string;
    rawToken: string;
    claims: {
      uuid: string;
    };
  };
}

interface BBGetTokensParams {
  clientId: string;
  clientSecret: string;
  scopes: string[];
  redirectURI: string;
  envType?: number;
  noCache?: boolean;
}

interface BBSignOnParams {
  clientId: string;
  scopes: string[];
  redirectURI: string;
  state: string;
  nonce: string;
}

declare const BB: {
  Apps: {
    Auth: {
      OAuth2: {
        getTokens(params: BBGetTokensParams): Promise<BBTokens>;
        signOn(params: BBSignOnParams): Promise<void>;
      };
    };
  };
};

const bbgClientId = 'APP.WEB.MNI';
const bbgClientSercret = 'LdZYaSpBs5RRB34sl0AwhvHUkvWdBgjPbN9WaHj2ld6gkefxyI';

const BBG_AUTH_MAX_RETRIES = 2;
const BBG_AUTH_RETRY_DELAY_MS = 3000;

export default function BbgPage() {
  const [state, setState] = useState<'LOGGED_IN' | 'LOGGING_IN' | 'ERROR' | 'NOT_BBG'>(
    'LOGGING_IN',
  );
  const [retryCount, setRetryCount] = useState(0);
  const [log, setLog] = useState<string[]>([]);
  const [slowLogin, setSlowLogin] = useState(false);
  const navigate = useNavigate();

  const addLog = (s: string) => {
    setLog([...log, s]);
  };

  useEffect(() => {
    if (state !== 'LOGGING_IN') return;

    setSlowLogin(false);

    const recentToken = (() => {
      try {
        const raw = authService.getCredentials()?.token?.access_token;
        if (!raw || !authService.tokenValid()) return false;
        const body = JSON.parse(atob(raw.split('.')[1]));
        const ageMs = Date.now() - (body.iat ?? 0) * 1000;
        return ageMs < 30 * 1000;
      } catch {
        return false;
      }
    })();

    // if recent token we don't check for permission changes
    if (recentToken) {
      console.log('[BBG] Token issued by a concurrent instance, skipping BBG SSO');
      setState('LOGGED_IN');
      navigate('/tabs', { replace: true });
      return;
    }

    const slowTimer = setTimeout(() => setSlowLogin(true), 15000);

    const subject = authService.getSubjectData() ?? { id: null, provider: 'mni|bbg' };

    const staleIframe = document.getElementById('bbauth');
    if (staleIframe) {
      console.warn('[BBG] Stale auth iframe found, removing before attempt');
      staleIframe.remove();
    }

    console.log(`[BBG] Starting auth attempt ${retryCount + 1}/${BBG_AUTH_MAX_RETRIES + 1}`, {
      noCache: retryCount > 0,
    });

    addLog('Checking BBG');
    try {
      console.log(BB.Apps.Auth.OAuth2);
    } catch (e) {
      setState('NOT_BBG');
      return;
    }

    addLog('Logging into  BBG');

    let retryTimeout: ReturnType<typeof setTimeout>;

    BB.Apps.Auth.OAuth2.getTokens({
      clientId: bbgClientId,
      clientSecret: bbgClientSercret,
      scopes: ['openid', 'profile'],
      redirectURI: window.location.href,
      noCache: retryCount > 0,
    }).then(
      async value => {
        addLog('Logged into  BBG');
        console.log('Received BBG token', value);

        try {
          const bbgUuid: string | undefined = value?.idToken?.claims?.uuid;
          let showBbgModal = false;
          if (bbgUuid) {
            const url = `${process.env.REACT_APP_SELECT_HOST}/api/auth/bbg/has-bbg-role/${encodeURIComponent(
              bbgUuid,
            )}`;
            const resp = await fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ access_token: value.idToken.rawToken }),
            });
            if (resp.ok) {
              const hasAnyPermission = await resp.json();
              showBbgModal = !hasAnyPermission;
            } else {
              console.error('Failed to check BBG role', resp.status, await resp.text());
            }
          }

          addLog('Logging into  MNI');
          await authService.loginWithBbgToken(value.idToken.rawToken);
          setState('LOGGED_IN');
          navigate('/tabs', { replace: true, state: { showBbgModal } });
          return;
        } catch (reason) {
          console.error('Error during BBG role check or token exchange', reason);
          analyticsService.recordAnalyticsEvent(AnalyticsEvent.LOGIN_FAILED, {
            subject,
            object: {
              type: 'bbg_login',
              error: reason instanceof Error ? reason.message : String(reason),
            },
            session: { id: null },
          });
          setState('ERROR');
        }
      },
      reason => {
        console.error('Error fetching BBG token', reason);
        const isTimeout =
          reason instanceof Error &&
          reason.message.includes('Timeout waiting for window location hash');
        if (isTimeout && retryCount < BBG_AUTH_MAX_RETRIES) {
          console.warn(`BBG auth timeout, retrying (${retryCount + 1}/${BBG_AUTH_MAX_RETRIES})...`);
          retryTimeout = setTimeout(() => setRetryCount(c => c + 1), BBG_AUTH_RETRY_DELAY_MS);
        } else {
          analyticsService.recordAnalyticsEvent(AnalyticsEvent.LOGIN_FAILED, {
            subject,
            object: {
              type: 'bbg_login',
              error: reason instanceof Error ? reason.message : String(reason),
            },
            session: { id: null },
          });
          setState('ERROR');
        }
      },
    );

    return () => {
      clearTimeout(slowTimer);
      clearTimeout(retryTimeout);
      const iframe = document.getElementById('bbauth');
      if (iframe) {
        console.warn('[BBG] Cleanup: removing auth iframe on effect teardown');
        iframe.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, retryCount]);

  if (state === 'NOT_BBG') {
    return <div className={'alert alert-danger'}>This is not a Bloomberg Environment</div>;
  }

  if (state === 'ERROR') {
    return <div className={'alert alert-danger'}>An error has occured</div>;
  }

  if (state === 'LOGGED_IN') return null;

  return (
    <>
      <style>{`
        @keyframes mni-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
      <div className={'m-5'}>
        <BarLoader color={theme.primary} width={'100%'} />
        <div className="text-center mt-3">
          {slowLogin ? 'Login is taking longer than usual, please wait....' : 'Logging in....'}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <img
            src="/MNI-icon.svg"
            alt="Logo"
            style={{
              width: '30%',
              maxHeight: '50%',
              objectFit: 'contain',
              animation: 'mni-pulse 2.2s ease-in-out infinite',
            }}
          />
        </div>
      </div>
    </>
  );
}
